import React from 'react'; 
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { NextStepsProvider } from './components/NextSteps2.0/NextStepsContext.jsx';
import store from './redux/store.js';
import { BrowserRouter, Routes, Route } from 'react-router-dom'; 
import Nav from './components/Nav.jsx';
import NavSide from './components/NavSide.jsx';
import StepRenderer from './components/StepRenderer.jsx';
import { useAuth } from './utils/AuthContext/AuthContext.jsx';
import useGoogleMaps from './loadGoogleMapsApi.js';
import { useMediaQuery } from 'react-responsive';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner.jsx';
import SEO from './utils/SEO.jsx';
  
import socketService from './services/socket.js';

// PAGES
import Login from './pages/Login.jsx';
import Register from './pages/Register.jsx';
import About from './pages/About.jsx';
import ContractForm from './pages/ContractForm.jsx';
import HomePage from './pages/Home.jsx'; 
import Forms from './pages/Forms.jsx';
import Professionals from './pages/Professionals.jsx';
import PacketSign from './components/PacketSigning/PacketSign.jsx';
import PacketComplete from './components/PacketSigning/PacketComplete.jsx';
import AmendmentPacketComplete from './components/PacketSigning/AmendmentPacketComplete.jsx';
import ContractFocus from './pages/ContractFocus.jsx';
import AmendExtend from './pages/AmendExtend.jsx';
import AmendFinal from './pages/AmendFinal.jsx';
import TermsofUse from './pages/TermsofUse.jsx';
import ForgotPassword from './components/ForgotPassword.jsx';
import ResetPassword from './components/ResetPassword.jsx';
import Privacy from './pages/Privacy.jsx';
import Profile from './pages/Profile.jsx';
import CallbackPage from './pages/CallbackPage.jsx'; 
import AdminPage from './components/AdministratorComponents/AdminPage.jsx';
import AuthSuccess from './pages/AuthSuccess.jsx';

// PROVIDERS
import { StepProvider } from './utils/StepContext.jsx'; 
import { FormProvider } from './utils/FormContext.jsx';
import { AuthProvider } from './utils/AuthContext/AuthContext.jsx'; 

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-shi-eggshell">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">Please try refreshing the page</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-shi-green text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Socket connection component
function SocketConnection() {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      socketService.connect();
    }

    return () => {
      if (isAuthenticated) {
        socketService.disconnect();
      }
    };
  }, [isAuthenticated]);

  return null;
}

function App() {
  const googleMapsLoaded = useGoogleMaps();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  if (!googleMapsLoaded) {
    return (
      <ErrorBoundary>
        <SEO 
          title="Loading"
          description="Loading Shishito - Smart Real Estate Contract Platform"
          noIndex={true}
        />
        <div><LoadingSpinner /></div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="bg-shi-eggshell h-screen">
        <BrowserRouter>
          <AuthProvider>
            <NextStepsProvider>
              <Provider store={store}>
                <StepProvider>
                  <FormProvider>
                    <SocketConnection />
                    <div className='flex bg-shi-eggshell min-h-screen'>
                      <NavSide />
                      <div className="flex-1 px-4 md:px-8 py-6 md:mt-0 mt-[55px]">
                        <Routes>
                          <Route path="/" element={<MainRoute />} />
                          <Route path="/contractform" element={<ContractForm />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/register" element={<Register />} />
                          <Route path="/about" element={<About />} />
                          <Route path="/termsofUse" element={<TermsofUse />} />
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/forms" element={<Forms />} />
                          <Route path="/newcontractform" element={<ContractForm />} />
                          <Route path="/formdata/:formId" element={<ContractForm />} />
                          <Route path="/packet/sign" element={<PacketSign />} />
                          <Route path="/packet/finish" element={<PacketComplete />} />
                          <Route path="/amendment/packet/finish" element={<AmendmentPacketComplete />} />
                          <Route path="/contract/:formId" element={<ContractFocus />} />
                          <Route path="/professionals" element={<Professionals />} />
                          <Route path="/amendextend" element={<AmendExtend />} />
                          <Route path="/amendment/:amendmentId" element={<AmendFinal /> } />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/reset-password/:token" element={<ResetPassword />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/callback" element={<CallbackPage />} />
                          <Route path="/auth/google/callback" element={<AuthSuccess />} />
                        </Routes>
                      </div>
                    </div>
                  </FormProvider>
                </StepProvider>
              </Provider>
            </NextStepsProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

function MainRoute() {
  const { isAuthenticated, isAdmin } = useAuth();

  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <SEO 
          title="Create Real Estate Offer"
          description="Start your real estate journey by creating a smart offer. Be your own agent with Shishito's intelligent contract platform."
          noIndex={false}
        />
        <ContractForm />
      </ErrorBoundary>
    );
  }

  if (isAdmin) {
    return (
      <ErrorBoundary>
        <SEO 
          title="Admin Dashboard"
          description="Shishito Admin Dashboard"
          noIndex={true}
        />
        <AdminPage />
      </ErrorBoundary>
    );
  }
  
  return (
    <ErrorBoundary>
      <SEO 
        title="Dashboard"
        description="Manage your real estate offers and contracts"
        noIndex={true}
      />
      <HomePage />
    </ErrorBoundary>
  );
} 

export default App;
